import React from 'react';

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import LabelChip from 'components/chip/LabelChip';
import VisibilityChip from 'components/chip/VisibilityChip';
import { ArticleSummary, ArticleVisibility } from 'stores/ArticleStoreAdapter';
import { formatDate } from 'utils/DateTimeFormatter';

import { formatAuthors, formatReadTime } from './articleUtils';

type Props = {
  article: ArticleSummary;
  onOpen: (articleId: string) => void;
  onEdit?: (articleId: string) => void;
  onDelete?: (article: ArticleSummary) => void;
  onChangeVisibility?: (article: ArticleSummary, visibility: ArticleVisibility) => void;
};

const VISIBILITY_OPTIONS: ArticleVisibility[] = ['PUBLIC', 'INTERNAL', 'PRIVATE'];

/**
 * 記事のリスト（一覧）表示の1行。カード表示と切り替えて使う横長レイアウト。
 * 1 行に タイトル/サブタイトル + ラベル・公開設定 + 著者・公開日・読了目安 を凝縮して表示する。
 */
const ArticleListItem: React.FC<Props> = ({
  article,
  onOpen,
  onEdit,
  onDelete,
  onChangeVisibility,
}) => {
  const [menuAnchor, setMenuAnchor] = React.useState<null | HTMLElement>(null);
  const [visibilityExpanded, setVisibilityExpanded] = React.useState(false);

  const showMenu = article.editable && (onEdit || onDelete || onChangeVisibility);
  const readTime = formatReadTime(article.charCount);
  const authorText = formatAuthors(article.authors);

  const closeMenu = () => {
    setMenuAnchor(null);
    setVisibilityExpanded(false);
  };

  return (
    <Box
      onClick={() => onOpen(article.articleId)}
      sx={{
        position: 'relative',
        px: 2,
        py: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'background-color 0.2s, border-color 0.2s',
        '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.main' },
      }}
    >
      <Stack direction="row" spacing={2} alignItems="center">
        {/* タイトル（優先。あふれは省略）＋ 公開設定・ラベル */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight={700} noWrap sx={{ flexShrink: 1, minWidth: 0 }}>
            {article.title}
          </Typography>
          <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <VisibilityChip visibility={article.visibility} />
            {article.labels.slice(0, 2).map((l) => (
              <LabelChip key={l.labelId} name={l.name} color={l.color} />
            ))}
            {article.labels.length > 2 && (
              <Typography variant="caption" color="text.disabled">
                +{article.labels.length - 2}
              </Typography>
            )}
          </Box>
        </Stack>

        {/* 著者（余白の中で出せるだけ表示。あふれは ...）。狭い画面では隠す */}
        {authorText && (
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ minWidth: 0, flexShrink: 1, display: { xs: 'none', sm: 'flex' } }}
          >
            <PersonOutlineIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
            <Typography variant="caption" color="text.secondary" noWrap>
              {authorText}
            </Typography>
          </Stack>
        )}

        {/* 読了目安・公開日（固定・右寄せ）。狭い画面では隠す */}
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ flexShrink: 0, display: { xs: 'none', sm: 'flex' } }}
        >
          {readTime && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <AccessTimeIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.secondary">
                {readTime}
              </Typography>
            </Stack>
          )}
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ minWidth: 76, textAlign: 'right' }}
          >
            {formatDate(article.createdAt)}
          </Typography>
        </Stack>

        {/* オーバーフローメニュー（editable のみ） */}
        {showMenu && (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setMenuAnchor(e.currentTarget);
            }}
            sx={{ flexShrink: 0 }}
          >
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        )}
      </Stack>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={(e) => {
          (e as React.MouseEvent)?.stopPropagation?.();
          closeMenu();
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: { borderRadius: 2, border: 1, borderColor: 'divider', minWidth: 180, mt: 0.5 },
          },
        }}
        // メニュー内クリックが行クリック（onOpen）に伝播しないようにする
        onClick={(e) => e.stopPropagation()}
      >
        {onEdit && (
          <MenuItem
            onClick={() => {
              closeMenu();
              onEdit(article.articleId);
            }}
          >
            <ListItemIcon>
              <EditOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>編集</ListItemText>
          </MenuItem>
        )}
        {onChangeVisibility && (
          <MenuItem onClick={() => setVisibilityExpanded((v) => !v)}>
            <ListItemIcon>
              <VisibilityOutlinedIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ variant: 'body2' }}>
              公開設定を変更
            </ListItemText>
          </MenuItem>
        )}
        {onChangeVisibility &&
          visibilityExpanded &&
          VISIBILITY_OPTIONS.map((v) => (
            <MenuItem
              key={v}
              selected={v === article.visibility}
              onClick={() => {
                closeMenu();
                if (v !== article.visibility) {
                  onChangeVisibility(article, v);
                }
              }}
              sx={{ pl: 4, py: 0.75 }}
            >
              <VisibilityChip visibility={v} />
            </MenuItem>
          ))}
        {onDelete && (
          <>
            <Divider />
            <MenuItem
              onClick={() => {
                closeMenu();
                onDelete(article);
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteOutlineIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primaryTypographyProps={{ variant: 'body2' }}>削除</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default ArticleListItem;
